import * as Dialog from '@radix-ui/react-dialog'
import type {
  LanguageType,
  PlayerRoleType,
  TournamentParticipationRecord,
  TournamentProgress,
} from '@repo/sdk'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSubscription } from '@trpc/tanstack-react-query'
import { ChevronRight, Loader2, RotateCcw, TriangleAlert, X } from 'lucide-react'
import { Fragment, useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { TournamentGameState } from '@/components/play/tournament-game-state'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { useAccountTimeZone } from '@/hooks/use-account-time-zone'
import { gameStartButtonClassName } from '@/lib/game-start-button-style'
import { useI18n } from '@/lib/i18n'
import { canHoldStream } from '@/lib/realtime-transport'
import { useTRPC } from '@/lib/trpc'
import { tournamentUrl } from './edition'

type Edition = NonNullable<
  inferRouterOutputs<AppRouter>['tournamentRouter']['getEdition']
>

type SavedStartRequest = {
  id: string
  role: PlayerRoleType
  language: LanguageType
  participationId?: string
}

type SavedStartSnapshot = {
  serialized: string | null
  request: SavedStartRequest | null
}

type ParticipationContext = {
  actorId: string
  editionId: string
}

type RetryFocusRequest = {
  actorId: string
  editionId: string
  participationId: string
  cancelled: boolean
}

type ForfeitTarget = ParticipationContext & {
  participationId: string
}

function readSavedStartRequest(storageKey: string): SavedStartSnapshot {
  let serialized: string | null = null
  try {
    serialized = localStorage.getItem(storageKey)
    const request: unknown = JSON.parse(serialized ?? 'null')
    if (request && typeof request === 'object'
      && 'id' in request && typeof request.id === 'string'
      && 'role' in request && typeof request.role === 'string'
      && 'language' in request && typeof request.language === 'string') {
      return { serialized, request: request as SavedStartRequest }
    }
  }
  catch {
    return { serialized, request: null }
  }
  return { serialized, request: null }
}

function updateSavedStartRequest(
  storageKey: string,
  snapshot: SavedStartSnapshot,
  request: SavedStartRequest | null,
) {
  try {
    if (localStorage.getItem(storageKey) !== snapshot.serialized)
      return false
    if (request)
      localStorage.setItem(storageKey, JSON.stringify(request))
    else
      localStorage.removeItem(storageKey)
    return true
  }
  catch {
    return false
  }
}

function reconcileSavedStartRequest(
  storageKey: string,
  snapshot: SavedStartSnapshot,
  progress: TournamentProgress,
  participationId?: string,
): SavedStartSnapshot {
  const saved = snapshot.request
  if (!saved)
    return snapshot
  const binding = progress.startRequests.find(
    request => request.clientRequestId === saved.id,
  )
  if (!binding
    || (participationId && binding.participationId !== participationId)) {
    return snapshot
  }
  const record = progress.records.find(
    item => item.id === binding.participationId,
  )
  if (!record)
    return snapshot
  if (record.state !== 'IN_PROGRESS') {
    updateSavedStartRequest(storageKey, snapshot, null)
    return readSavedStartRequest(storageKey)
  }
  if (saved.participationId !== binding.participationId) {
    const request = { ...saved, participationId: binding.participationId }
    updateSavedStartRequest(storageKey, snapshot, request)
    return readSavedStartRequest(storageKey)
  }
  return snapshot
}

function roleKey(role: PlayerRoleType) {
  return `game.${role}`
}

export function TournamentParticipation({
  edition,
  onRules,
  variant = 'page',
  role: launcherRole,
  onProgress,
}: {
  edition: Edition
  onRules: () => void
  variant?: 'page' | 'launcher'
  role?: PlayerRoleType
  onProgress?: (progress: TournamentProgress | undefined) => void
}) {
  const { language, t } = useI18n()
  const { actor, authResolved } = useAuth()
  const accountCalendar = useAccountTimeZone()
  const actorId = actor?.id
  const trpc = useTRPC()
  const navigate = useNavigate()
  const forfeitDialogId = useId()
  const progressOptions = trpc.tournamentRouter.getProgress.queryOptions({
    editionId: edition.id,
  })
  const progressContext = {
    ...progressOptions.queryKey[1],
    actorId: actor?.id ?? null,
  }
  const progressQuery = useQuery({
    ...progressOptions,
    queryKey: [progressOptions.queryKey[0], progressContext],
    enabled: !!actor,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  })
  const start = useMutation(trpc.tournamentRouter.start.mutationOptions())
  const retry = useMutation(trpc.tournamentRouter.retry.mutationOptions())
  const forfeit = useMutation(trpc.tournamentRouter.forfeit.mutationOptions())
  const retryButtonRef = useRef<HTMLButtonElement>(null)
  const continueLinkRef = useRef<HTMLAnchorElement>(null)
  const participationRef = useRef<HTMLDivElement>(null)
  const forfeitButtonRef = useRef<HTMLButtonElement>(null)
  const forfeitCancelRef = useRef<HTMLButtonElement>(null)
  const startButtonRef = useRef<HTMLButtonElement>(null)
  const forfeitTargetRef = useRef<ForfeitTarget | null>(null)
  const pendingForfeitRef = useRef<ForfeitTarget | null>(null)
  const forfeitFocusContextRef = useRef<ForfeitTarget | null>(null)
  const startOperationRef = useRef<ParticipationContext | null>(null)
  const participationContextRef = useRef({
    actorId: actor?.id ?? null,
    editionId: edition.id,
  })
  participationContextRef.current = {
    actorId: actor?.id ?? null,
    editionId: edition.id,
  }
  const [forfeitTarget, setForfeitTarget] = useState<ForfeitTarget | null>(null)
  const [forfeitOpen, setForfeitOpen] = useState(false)
  const [forfeitPending, setForfeitPending] = useState(false)
  const [forfeitFailed, setForfeitFailed] = useState(false)
  const retryFocusRequestRef = useRef<RetryFocusRequest | null>(null)
  const [failedRetryFocusRequest, setFailedRetryFocusRequest]
    = useState<RetryFocusRequest | null>(null)
  const roles = edition.configuration.categories.flatMap(
    category => category.allowedRoles,
  )
  const godCategory = edition.configuration.categories.find(
    category => !category.allowedRoles.includes('mechanical_wolf'),
  )
  const [internalRole, setSelectedRole] = useState<PlayerRoleType>(
    roles[0] ?? 'psychic',
  )
  const selectedRole = launcherRole ?? internalRole
  const [failed, setFailed] = useState(false)
  const [retryFailed, setRetryFailed] = useState(false)
  const progress = progressQuery.data
  useEffect(() => {
    onProgress?.(progress)
  }, [progress, onProgress])
  const category = progress?.categories.find(item =>
    item.allowedRoles.includes(selectedRole),
  )
  const current = progress?.current
  const { refetch: refetchProgress } = progressQuery
  const scopedForfeitTarget = forfeitTarget?.actorId === actor?.id
    && forfeitTarget?.editionId === edition.id
    ? forfeitTarget
    : null
  const scopedForfeitPending = !!scopedForfeitTarget && forfeitPending

  useEffect(() => {
    if (!actorId || !progressQuery.isSuccess || !progress
      || progress.editionId !== edition.id) {
      return
    }
    const storageKey = `tournament:start:${edition.id}:${actorId}`
    reconcileSavedStartRequest(
      storageKey,
      readSavedStartRequest(storageKey),
      progress,
    )
  }, [actorId, edition.id, progress, progressQuery.dataUpdatedAt, progressQuery.isSuccess])

  useEffect(() => {
    startOperationRef.current = null
    forfeitTargetRef.current = null
    pendingForfeitRef.current = null
    setForfeitTarget(null)
    setForfeitOpen(false)
    setForfeitPending(false)
    setForfeitFailed(false)
    return () => {
      startOperationRef.current = null
      forfeitTargetRef.current = null
      pendingForfeitRef.current = null
    }
  }, [actor?.id, edition.id])

  useEffect(() => {
    setRetryFailed(false)
  }, [actor?.id, current?.id, edition.id])

  useEffect(() => {
    const otherControl = (target: EventTarget | null) => {
      const control = target instanceof Element
        ? target.closest(
            'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          )
        : null
      return !!control && control !== retryButtonRef.current
    }
    const onPointerDown = (event: PointerEvent) => {
      const request = retryFocusRequestRef.current
      if (request && otherControl(event.target))
        request.cancelled = true
    }
    const onKeyDown = (event: KeyboardEvent) => {
      const request = retryFocusRequestRef.current
      if (request && (event.key === 'Tab'
        || event.key === 'Escape'
        || otherControl(event.target))) {
        request.cancelled = true
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [])

  useEffect(() => {
    const request = failedRetryFocusRequest
    if (!request || retry.isPending)
      return
    if (request.cancelled
      || request.actorId !== actor?.id
      || request.editionId !== edition.id
      || (current && request.participationId !== current.id)) {
      if (retryFocusRequestRef.current === request)
        retryFocusRequestRef.current = null
      setFailedRetryFocusRequest(null)
      return
    }
    let settledFrame: number | null = null
    const frame = requestAnimationFrame(() => {
      settledFrame = requestAnimationFrame(() => {
        if (retryFocusRequestRef.current !== request || request.cancelled)
          return
        const target = retryButtonRef.current
          ?? continueLinkRef.current
          ?? document.getElementById(`tournament-records-${edition.id}`)
        target?.focus()
        retryFocusRequestRef.current = null
        setFailedRetryFocusRequest(null)
      })
    })
    return () => {
      cancelAnimationFrame(frame)
      if (settledFrame !== null)
        cancelAnimationFrame(settledFrame)
    }
  }, [actor?.id, current, edition.id, failedRetryFocusRequest, retry.isPending])

  useEffect(() => {
    const lifecycle = progress?.lifecycle
    if (!lifecycle?.nextTransitionAt)
      return
    const remaining
      = Date.parse(lifecycle.nextTransitionAt) - Date.parse(lifecycle.serverNow)
    const timer = window.setTimeout(() => {
      void refetchProgress()
    }, Math.min(Math.max(remaining + 100, 100), 2_147_483_647))
    return () => window.clearTimeout(timer)
  }, [progress?.lifecycle, refetchProgress])

  useSubscription(
    trpc.playRouter.onGameChanged.subscriptionOptions(
      { gameId: current?.gameId ?? '' },
      {
        enabled: !!current && canHoldStream(actor, authResolved),
        onData: () => {
          void refetchProgress()
        },
        onConnectionStateChange: (connection) => {
          if (connection.state === 'pending')
            void refetchProgress()
        },
      },
    ),
  )

  const recover = async () => {
    if (!actor || !current || retry.isPending)
      return
    const focusRequest = document.activeElement === retryButtonRef.current
      ? {
          actorId: actor.id,
          editionId: edition.id,
          participationId: current.id,
          cancelled: false,
        }
      : null
    retryFocusRequestRef.current = focusRequest
    setFailedRetryFocusRequest(null)
    setRetryFailed(false)
    try {
      await accountCalendar.initialize()
      const result = await retry.mutateAsync({ editionId: edition.id })
      await refetchProgress()
      retryFocusRequestRef.current = null
      navigate(
        `/play/${result.gameId}?tournament=${encodeURIComponent(edition.id)}`,
      )
    }
    catch {
      setRetryFailed(true)
      await refetchProgress()
      if (focusRequest && retryFocusRequestRef.current === focusRequest)
        setFailedRetryFocusRequest(focusRequest)
    }
  }
  const changeForfeitOpen = (open: boolean) => {
    if (open) {
      if (!actor || !current || retry.isPending)
        return
      const target = {
        actorId: actor.id,
        editionId: edition.id,
        participationId: current.id,
      }
      forfeitTargetRef.current = target
      forfeitFocusContextRef.current = target
      setForfeitTarget(target)
      setForfeitFailed(false)
      setForfeitOpen(true)
      return
    }
    if (pendingForfeitRef.current === forfeitTargetRef.current
      && pendingForfeitRef.current) {
      return
    }
    forfeitTargetRef.current = null
    setForfeitTarget(null)
    setForfeitOpen(false)
    setForfeitFailed(false)
  }
  const closeConfirmedForfeit = () => {
    forfeitTargetRef.current = null
    setForfeitTarget(null)
    setForfeitOpen(false)
  }
  const confirmForfeit = async () => {
    const target = forfeitTargetRef.current
    if (!target || pendingForfeitRef.current === target)
      return
    const isCurrentTarget = () => forfeitTargetRef.current === target
      && participationContextRef.current.actorId === target.actorId
      && participationContextRef.current.editionId === target.editionId
    if (!isCurrentTarget())
      return
    pendingForfeitRef.current = target
    const storageKey = `tournament:start:${target.editionId}:${target.actorId}`
    const savedStart = readSavedStartRequest(storageKey)
    setForfeitPending(true)
    setForfeitFailed(false)
    try {
      await forfeit.mutateAsync({
        editionId: target.editionId,
        participationId: target.participationId,
      })
      if (!isCurrentTarget())
        return
      const refreshed = await refetchProgress()
      if (!isCurrentTarget())
        return
      if (refreshed.isError)
        throw refreshed.error
      if (refreshed.data?.editionId === target.editionId) {
        reconcileSavedStartRequest(
          storageKey,
          savedStart,
          refreshed.data,
          target.participationId,
        )
      }
      closeConfirmedForfeit()
    }
    catch {
      if (!isCurrentTarget())
        return
      await refetchProgress().catch(() => {})
      if (isCurrentTarget())
        setForfeitFailed(true)
    }
    finally {
      if (pendingForfeitRef.current === target) {
        pendingForfeitRef.current = null
        setForfeitPending(false)
      }
    }
  }
  const launch = async () => {
    if (!actor || !progress || current || start.isPending)
      return
    setFailed(false)
    const context = { actorId: actor.id, editionId: edition.id }
    startOperationRef.current = context
    const isCurrentOperation = () => startOperationRef.current === context
      && participationContextRef.current.actorId === context.actorId
      && participationContextRef.current.editionId === context.editionId
    const storageKey = `tournament:start:${context.editionId}:${context.actorId}`
    let snapshot = readSavedStartRequest(storageKey)
    if (progressQuery.isSuccess && progress.editionId === context.editionId) {
      snapshot = reconcileSavedStartRequest(storageKey, snapshot, progress)
    }
    let saved = snapshot.request
    if (!saved || saved.role !== selectedRole || saved.language !== language) {
      const request: SavedStartRequest = {
        id: crypto.randomUUID(),
        role: selectedRole,
        language: language as LanguageType,
      }
      if (!updateSavedStartRequest(storageKey, snapshot, request)) {
        setFailed(true)
        return
      }
      saved = request
      snapshot = { serialized: JSON.stringify(request), request }
    }
    try {
      await accountCalendar.initialize()
      const result = await start.mutateAsync({
        editionId: context.editionId,
        role: saved.role,
        language: saved.language,
        clientRequestId: saved.id,
      })
      if (!isCurrentOperation())
        return
      const latest = readSavedStartRequest(storageKey)
      if (latest.request?.id === saved.id)
        updateSavedStartRequest(storageKey, latest, null)
      navigate(
        `/play/${result.gameId}?tournament=${encodeURIComponent(context.editionId)}`,
      )
    }
    catch {
      if (!isCurrentOperation())
        return
      setFailed(true)
      const refreshed = await refetchProgress()
      if (isCurrentOperation() && refreshed.isSuccess
        && refreshed.data.editionId === context.editionId) {
        reconcileSavedStartRequest(storageKey, snapshot, refreshed.data)
      }
    }
  }
  const recordState = (record: TournamentParticipationRecord) => {
    if (record.state === 'COMPLETED_WIN')
      return t('tournamentParticipation.win')
    if (record.state === 'COMPLETED_LOSS')
      return t('tournamentParticipation.loss')
    if (record.state === 'FORFEITED_LOSS')
      return t('tournamentParticipation.forfeitedLoss')
    if (record.state === 'VOID')
      return t('tournamentVoid.title')
    return t('dashboard.games.statuses.in_progress')
  }

  return (
    <div ref={participationRef} data-testid="tournament-participation" data-variant={variant} className="space-y-5 text-start">
      {progressQuery.isPending && <p role="status">{t('forum.loading')}</p>}
      {progressQuery.isError && <div role="alert"><p>{t('forum.errorGeneric')}</p><Button type="button" variant="outline" className="mt-3 min-h-11" onClick={() => { void progressQuery.refetch() }}>{t('play.retry.button')}</Button></div>}
      {progress && (
        <div className={variant === 'launcher' ? 'space-y-4' : 'grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]'}>
          {variant !== 'launcher' && (
            <section className="min-w-0 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6 lg:col-start-1 lg:row-start-1">
              <h2 className="text-xl font-semibold">{t('tournamentParticipation.myGames')}</h2>
              <div data-testid="tournament-progress" role="status" aria-live="polite" className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-4">
                {[
                  { value: `${progress.completed} / ${progress.total}`, label: t('tournamentParticipation.completedLabel') },
                  { value: progress.remaining, label: t('tournamentParticipation.remainingLabel') },
                  { value: progress.wins, label: t('tournamentParticipation.win') },
                  { value: progress.losses, label: t('tournamentParticipation.loss') },
                ].map(item => <div key={item.label}><p className="text-3xl font-bold tabular-nums">{item.value}</p><p className="mt-1 text-xs text-muted-foreground">{item.label}</p></div>)}
              </div>
              <div className="mt-6 grid grid-cols-2 gap-5">{progress.categories.map(item => <div key={item.id} data-testid={`tournament-category-${item.id}`}><p className="flex flex-wrap justify-between gap-2 text-sm"><span>{t(item.allowedRoles.includes('mechanical_wolf') ? 'game.mechanical_wolf' : 'tournamentParticipation.godRoleCategory')}</span><span className="tabular-nums">{item.completed} / {item.limit}</span></p><div aria-hidden="true" className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{ width: `${item.limit > 0 ? item.completed / item.limit * 100 : 0}%` }} /></div><p data-testid={`tournament-remaining-${item.id}`} className="mt-2 text-xs text-muted-foreground">{t('tournamentParticipation.remaining', { count: item.remaining })}{item.inProgress > 0 && ` · ${t('tournamentParticipation.inProgress', { count: item.inProgress })}`}</p></div>)}</div>
              <p className="mt-4 text-xs text-muted-foreground">{t('tournament.quotaIndependence')}</p>
            </section>
          )}
          <section className={variant === 'launcher' ? 'space-y-4' : 'space-y-4 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1'}>
            {variant !== 'launcher' && <h2 className="text-xl font-semibold">{t(current ? 'tournamentParticipation.currentGame' : 'tournamentParticipation.chooseRole')}</h2>}
            {progress.recent?.state === 'VOID' && (
              <TournamentGameState
                state="void"
                canContinue={progress.lifecycle.competitionOpen
                  && !current
                  && progress.remaining > 0}
              />
            )}
            {current ? (
              <div className="space-y-3" data-testid="tournament-current">
                <h4 className="font-semibold">
                  {t('tournamentParticipation.currentGame')}
                </h4>
                <p>
                  {t('tournamentParticipation.currentRole', {
                    role: t(roleKey(current.role)),
                  })}
                </p>
                {current.retryLater && (
                  <TournamentGameState state="retry">
                    {retryFailed && (
                      <p
                        role="alert"
                        data-testid="tournament-retry-error"
                        className="text-base leading-6 text-primary"
                      >
                        {t('play.retry.failed')}
                      </p>
                    )}
                    <Button
                      ref={retryButtonRef}
                      type="button"
                      data-testid="tournament-retry"
                      disabled={retry.isPending}
                      aria-busy={retry.isPending}
                      onClick={() => {
                        void recover()
                      }}
                      className={`${gameStartButtonClassName} h-auto min-h-12 whitespace-normal text-base focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
                    >
                      {retry.isPending ? (
                        <Loader2
                          className="size-4 animate-spin"
                          aria-hidden="true"
                        />
                      ) : (
                        <RotateCcw className="size-4" aria-hidden="true" />
                      )}
                      {t(
                        retry.isPending
                          ? 'play.retry.retrying'
                          : 'play.retry.button',
                      )}
                    </Button>
                  </TournamentGameState>
                )}
                <Button
                  asChild
                  className={current.retryLater
                    ? 'h-auto min-h-12 w-full whitespace-normal border border-border bg-muted/50 text-base text-foreground hover:bg-card focus-visible:ring-2 focus-visible:ring-ring'
                    : `${gameStartButtonClassName} min-h-12 w-full`}
                >
                  <Link
                    ref={continueLinkRef}
                    data-testid="tournament-continue"
                    to={`/play/${current.gameId}?tournament=${encodeURIComponent(edition.id)}`}
                  >
                    {t('tournamentParticipation.continue')}
                  </Link>
                </Button>
                <Button
                  ref={forfeitButtonRef}
                  type="button"
                  data-testid="tournament-forfeit"
                  disabled={retry.isPending}
                  aria-haspopup="dialog"
                  aria-expanded={!!scopedForfeitTarget && forfeitOpen}
                  aria-controls={scopedForfeitTarget && forfeitOpen
                    ? forfeitDialogId
                    : undefined}
                  onClick={() => changeForfeitOpen(true)}
                  className="h-auto min-h-11 w-full whitespace-normal border border-border bg-muted/50 text-base text-primary hover:bg-card focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('tournamentForfeit.confirm')}
                </Button>
              </div>
            ) : progress.completed === progress.total ? (
              <div role="status" className="space-y-3">
                <p className="font-semibold">
                  {t('tournamentParticipation.allCompleted')}
                </p>
                <p>{t('tournamentParticipation.thanks')}</p>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="tournament-view-mine"
                  className="min-h-11 w-full"
                  onClick={() => {
                    variant === 'launcher'
                      ? navigate(tournamentUrl(edition.id))
                      : document.getElementById(`tournament-records-${edition.id}`)?.focus()
                  }}
                >
                  {t('tournamentParticipation.viewMine')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <fieldset
                  hidden={variant === 'launcher'}
                  disabled={start.isPending || !progress.lifecycle.competitionOpen}
                >
                  <legend className="mb-3 text-lg font-semibold">
                    {t('tournamentParticipation.chooseRole')}
                  </legend>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    {roles.map(role => (
                      <button
                        key={role}
                        type="button"
                        aria-pressed={role === selectedRole}
                        disabled={(progress.categories.find(item =>
                          item.allowedRoles.includes(role),
                        )?.remaining ?? 0) === 0}
                        aria-describedby={(progress.categories.find(item =>
                          item.allowedRoles.includes(role),
                        )?.remaining ?? 0) === 0
                          ? `tournament-exhausted-${edition.id}-${progress.categories.find(item => item.allowedRoles.includes(role))?.id}`
                          : undefined}
                        data-testid={`tournament-role-${role}`}
                        onClick={() => setSelectedRole(role)}
                        className={`${role === 'mechanical_wolf' ? 'col-span-2 sm:col-span-1' : ''} min-h-12 min-w-0 whitespace-normal break-words rounded-lg border p-3 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${role === selectedRole ? 'border-border bg-primary/10 shadow-sm' : 'border-border bg-muted/50 hover:bg-card'}`}
                      >
                        {t(roleKey(role))}
                      </button>
                    ))}
                  </div>
                </fieldset>
                {variant !== 'launcher' && godCategory && (
                  <p className="text-center text-sm text-muted-foreground">
                    {t('tournamentParticipation.sharedQuota', { limit: godCategory?.limit ?? 0 })}
                  </p>
                )}
                {progress.categories.filter(item => item.remaining === 0).map(item => (
                  <p
                    key={item.id}
                    id={`tournament-exhausted-${edition.id}-${item.id}`}
                    data-testid={`tournament-exhausted-${item.id}`}
                    className="text-sm text-primary"
                  >
                    {t(item.allowedRoles.includes('mechanical_wolf')
                      ? 'tournamentParticipation.mechanicalWolfExhausted'
                      : 'tournamentParticipation.godRolesExhausted')}
                  </p>
                ))}
                {failed && (
                  <p
                    role="alert"
                    data-testid="tournament-start-error"
                    className="text-primary"
                  >
                    {t('game.room.startFailed')}
                  </p>
                )}
                <Button
                  ref={startButtonRef}
                  type="button"
                  data-testid="tournament-start"
                  disabled={!progress.lifecycle.competitionOpen
                    || !category?.remaining
                    || start.isPending}
                  onClick={() => {
                    void launch()
                  }}
                  className={`${gameStartButtonClassName} min-h-12 w-full`}
                >
                  {start.isPending && (
                    <Loader2
                      className="me-2 size-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {t(
                    start.isPending
                      ? 'forum.loading'
                      : variant === 'launcher' ? 'game.startGame' : 'tournamentParticipation.start',
                  )}
                </Button>
              </div>
            )}

            {variant !== 'launcher' && <><p className="text-sm text-muted-foreground">{t('tournamentParticipation.alternation')}</p><div className="border-t border-border pt-4"><p className="text-sm text-muted-foreground">{t('tournamentForfeit.rule')}</p><p className="mt-2 text-sm text-muted-foreground">{t('tournamentRules.gameLimit', { total: progress.total })}</p><button type="button" data-testid="tournament-participation-rules" onClick={onRules} className="mt-3 flex min-h-11 items-center gap-1 text-sm font-semibold">{t('tournamentRules.viewCompetitionRules')}<ChevronRight className="size-4" aria-hidden="true" /></button></div></>}
          </section>
          {variant !== 'launcher' && (
            <section id={`tournament-records-${edition.id}`} tabIndex={-1} aria-labelledby={`tournament-records-title-${edition.id}`} data-testid="tournament-records" className="min-w-0 rounded-2xl border border-border bg-card p-5 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-6 lg:col-start-1 lg:row-start-2">
              <h2 id={`tournament-records-title-${edition.id}`} className="mb-4 text-xl font-semibold">{t('tournamentParticipation.myGames')}</h2>
              {progress.records.length > 0 ? (
                <ol className="divide-y divide-border">{progress.records.map((record, index) => (
                  <li key={record.id} data-testid="tournament-record" data-participation-id={record.id} data-game-id={record.gameId} data-category-id={record.categoryId} data-state={record.state} className="grid grid-cols-2 items-center gap-2 py-4 text-sm sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
                    <div data-testid={index === 0 ? 'tournament-recent' : undefined}><p className="font-semibold">{t('tournamentParticipation.gameNumber', { number: progress.records.length - index })}</p><p className="mt-1 text-xs text-muted-foreground">{new Intl.DateTimeFormat(language, { timeZone: edition.timezone, dateStyle: 'short', timeStyle: 'short' }).format(new Date(record.createdAt))}</p></div>
                    <p>{t(roleKey(record.role))}</p>
                    <div><span className={`inline-block rounded-md px-2 py-1 text-xs font-semibold ${record.state === 'COMPLETED_WIN' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : record.state === 'COMPLETED_LOSS' || record.state === 'FORFEITED_LOSS' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-muted text-foreground'}`}>{recordState(record)}</span>{record.retryLater && <p className="mt-1 text-xs">{t('tournamentRecovery.tryLater')}</p>}{record.state === 'VOID' && <p className="mt-1 text-xs text-muted-foreground">{t('tournamentVoid.returned')} · {t('tournamentVoid.noResult')}</p>}</div>
                    <Link className="flex min-h-11 items-center justify-end gap-1 font-semibold" to={`/play/${record.gameId}?tournament=${encodeURIComponent(edition.id)}`}>{t('publicPlayerProfile.viewGame')}<ChevronRight className="size-4" aria-hidden="true" /></Link>
                  </li>
                ))}
                </ol>
              ) : <p className="text-sm text-muted-foreground">{t('dashboard.games.empty')}</p>}
              <p className="mt-4 border-t border-border pt-4 text-xs text-muted-foreground">{t('tournamentParticipation.publication')}</p>
            </section>
          )}
        </div>
      )}
      <Dialog.Root
        open={!!scopedForfeitTarget && forfeitOpen}
        onOpenChange={changeForfeitOpen}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/70" />
          <Dialog.Content
            id={forfeitDialogId}
            data-testid="tournament-forfeit-dialog"
            data-participation-id={scopedForfeitTarget?.participationId}
            className="fixed left-1/2 top-1/2 z-[60] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto overscroll-contain rounded-2xl border border-border bg-card p-5 text-center text-foreground shadow-2xl outline-none sm:p-8"
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              forfeitCancelRef.current?.focus()
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              const context = forfeitFocusContextRef.current
              if (!context)
                return
              requestAnimationFrame(() => {
                if (!participationRef.current?.isConnected
                  || participationContextRef.current.actorId !== context.actorId
                  || participationContextRef.current.editionId !== context.editionId) {
                  return
                }
                const target = forfeitButtonRef.current
                  ?? (startButtonRef.current?.disabled
                    ? null
                    : startButtonRef.current)
                  ?? document.getElementById(`tournament-records-${context.editionId}`)
                  ?? (variant === 'launcher' ? document.getElementById('role') : null)
                target?.focus()
              })
            }}
            onEscapeKeyDown={(event) => {
              if (scopedForfeitPending)
                event.preventDefault()
            }}
            onInteractOutside={event => event.preventDefault()}
          >
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={t('common.close')}
                data-testid="tournament-forfeit-close"
                disabled={scopedForfeitPending}
                className="absolute end-2 top-2 flex size-11 items-center justify-center rounded-md hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                <X className="size-6" aria-hidden="true" />
              </button>
            </Dialog.Close>
            <TriangleAlert
              className="mx-auto my-4 size-12 text-primary"
              aria-hidden="true"
            />
            <Dialog.Title className="px-4 text-2xl font-semibold leading-snug sm:text-3xl">
              {t('tournamentForfeit.title')}
            </Dialog.Title>
            <Dialog.Description asChild>
              <div className="mt-4 space-y-5 text-base leading-7">
                <p className="font-semibold">{t('tournamentForfeit.rule')}</p>
                <p className="text-muted-foreground">
                  {t('tournamentForfeit.warning').split('<br>').map((line, index) => (
                    <Fragment key={line}>
                      {index > 0 && <br />}
                      {line}
                    </Fragment>
                  ))}
                </p>
              </div>
            </Dialog.Description>
            {forfeitFailed && scopedForfeitTarget && (
              <p
                role="alert"
                data-testid="tournament-forfeit-error"
                className="mt-4 text-base leading-6 text-primary"
              >
                {t('forum.errorGeneric')}
              </p>
            )}
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Dialog.Close asChild>
                <Button
                  ref={forfeitCancelRef}
                  type="button"
                  data-testid="tournament-forfeit-cancel"
                  disabled={scopedForfeitPending}
                  className="h-auto min-h-12 whitespace-normal border border-border bg-muted/50 text-base text-foreground hover:bg-card focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('tournamentForfeit.continue')}
                </Button>
              </Dialog.Close>
              <Button
                type="button"
                data-testid="tournament-forfeit-confirm"
                disabled={scopedForfeitPending}
                aria-busy={scopedForfeitPending}
                onClick={() => {
                  void confirmForfeit()
                }}
                className="h-auto min-h-12 whitespace-normal border-2 border-border bg-primary/10 text-base text-foreground hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {scopedForfeitPending && (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                )}
                {t(scopedForfeitPending
                  ? 'forum.loading'
                  : 'tournamentForfeit.confirm')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
